/**
 * storyExportApi — PDF export endpoint.
 *
 * Toggle via REACT_APP_STORY_EXPORT_API env flag:
 *   unset / "live" → real multipart POST
 *   "mock"         → offline fixture from storyExportApi.mock.ts
 *
 * To switch mock → live after backend merges: remove the isMock() branch
 * or set REACT_APP_STORY_EXPORT_API=live in .env.local.
 */
import { isMockApiFlag } from "../apiMode";
import { API_ENDPOINTS, apiService } from "../api";
import { mockExportStoryPdf } from "./storyExportApi.mock";
import type { PdfExportResponse } from "./storyExport.types";
import { sendSlackMessage } from "../notifications/slackApi";
import type { SlackStoryAttachmentRef } from "../notifications/slackApi";
import { getApiErrorMessage } from "../apiErrors";

const isMock = (): boolean =>
  isMockApiFlag(process.env.REACT_APP_STORY_EXPORT_API);

export async function exportStoryPdf(
  workspaceId: string,
  storyId: string,
  file: File,
): Promise<PdfExportResponse> {
  if (isMock()) return mockExportStoryPdf(workspaceId, storyId, file);

  const formData = new FormData();
  formData.append("file", file);

  return apiService.postFormData<PdfExportResponse>(
    API_ENDPOINTS.stories.artifactMemoExport(workspaceId, storyId),
    formData,
  );
}

export interface SlackChannelSendResult {
  channelId: string;
  success: boolean;
  error?: string;
}

/** Shared by both send functions below — same allSettled + result-shaping,
 * just a different way of getting Slack the attachment. */
async function sendToSlackChannels(
  channelIds: string[],
  message: string,
  perChannel: (channelId: string) => Promise<unknown>,
): Promise<SlackChannelSendResult[]> {
  if (channelIds.length === 0) {
    throw new Error("Select at least one Slack channel before sending.");
  }

  // `allSettled` (not `all`) so one bad channel doesn't mask the others
  // succeeding — every channel gets its own result instead of the whole
  // batch rejecting together.
  const settled = await Promise.allSettled(channelIds.map(perChannel));

  // Always resolve, never throw on a full failure — the caller (useDeliverStory)
  // is the single place that turns `results` into a user-facing message, so
  // full-failure and partial-failure share one formatter (with channel names,
  // not raw ids) instead of two different code paths drifting apart.
  return settled.map((outcome, i) =>
    outcome.status === "fulfilled"
      ? { channelId: channelIds[i], success: true }
      : {
          channelId: channelIds[i],
          success: false,
          error: getApiErrorMessage(outcome.reason, "Unknown error"),
        },
  );
}

// TODO: Workaround fallback — remove the artifactLatestDownload branch once
// canvas capture always produces an in-memory file (unrelated to Slack
// delivery itself, just how the PDF bytes are sourced).
//
// Used for the "memo"/"story" artifact deliveries — unchanged from before
// server-side deck rendering existed. "presentation" artifact deliveries use
// sendPresentationDeckToSlack below instead.
export async function sendStoryToSlack(
  workspaceId: string,
  storyId: string,
  message: string,
  channelIds: string[],
  format = "pdf",
  preExportedFile?: File,
): Promise<SlackChannelSendResult[]> {
  let file: File;

  if (preExportedFile) {
    file = preExportedFile;
  } else {
    // TODO: Workaround fallback — remove once canvas capture always produces in-memory file
    const artifactResponse = await apiService["api"].get(
      API_ENDPOINTS.stories.artifactLatestDownload(workspaceId, storyId, format),
      { responseType: "blob" },
    );
    const blob: Blob = artifactResponse.data;
    file = new File([blob], `story.${format}`, { type: blob.type || "application/pdf" });
  }

  return sendToSlackChannels(channelIds, message, (channelId) =>
    sendSlackMessage(channelId, message, file),
  );
}

/** "presentation" artifact deliveries only, format "pdf" or "pptx" — the
 * backend renders the deck itself (story_presentation_service.render_deck)
 * per channel send, no client-side export step and no GCS round-trip. The
 * same story_id/workspace_id/format the backend needs here is exactly what
 * a scheduled delivery would pass too, just without a "Send Now" click
 * triggering it. */
export async function sendPresentationDeckToSlack(
  workspaceId: string,
  storyId: string,
  message: string,
  channelIds: string[],
  format: "pdf" | "pptx" = "pdf",
  deck: Pick<SlackStoryAttachmentRef, "theme" | "artifactId"> = {},
): Promise<SlackChannelSendResult[]> {
  // With an artifactId every channel gets that one rendered file — no
  // per-channel render.
  const storyRef: SlackStoryAttachmentRef = { storyId, workspaceId, format, ...deck };
  return sendToSlackChannels(channelIds, message, (channelId) =>
    sendSlackMessage(channelId, message, undefined, storyRef),
  );
}
